import Link from 'next/link'
import { useMemo } from 'react'

import { Icon } from '@wartek-id/icon-v2'
import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { getCta } from 'app/KinerjaShared/components/CardKSSchool/utils'

import type { KinerjaRoleType } from 'types/kinerja'
import type {
  ConfirmationStatusType,
  SatuanPendidikanType,
} from 'types/kinerja-pelaksanaan'

export type CardKSSchoolProps = {
  confirmationPath?: string
  confirmationStatus: ConfirmationStatusType
  nextPath?: string
  nextText: string
  npsn: string
  schoolName: string
  schoolStatus: SatuanPendidikanType
  schoolRole: KinerjaRoleType
  onNextClick?: () => void
  onConfirmClick?: () => void
}

export function CardKSSchool({
  confirmationPath,
  confirmationStatus,
  nextPath,
  nextText,
  npsn,
  schoolName,
  schoolStatus,
  onNextClick,
  onConfirmClick,

  schoolRole,
}: CardKSSchoolProps) {
  // --------------------------------
  // Handle CTA
  // --------------------------------
  const cta = getCta({
    schoolStatus,
    confirmationStatus,
    confirmationPath,
    nextPath,
    nextText,
    confirmationText: 'Konfirmasi Data',
    onNextClick,
    onConfirmClick,
  })

  const ksLabel = useMemo(() => {
    switch (schoolRole) {
      case 'KS_DEFINITIF':
        return 'Kepala Sekolah Definitif'
      case 'KS_PLT':
        return 'Plt. Kepala Sekolah'
      case 'KS_DEFINITIF_SPNF':
        return 'Kepala Sekolah Non Formal Definitif'
      case 'KS_PLT_SPNF':
        return 'Plt. Kepala Sekolah Non Formal'
      default:
        return '-'
    }
  }, [schoolRole])

  return (
    <div className="p-6 flex items-start justify-between gap-4 border border-subdued rounded-lg">
      <div className="space-y-1">
        <Text variant="heading-sm" className="overflow-wrap-anywhere">
          {schoolName}
        </Text>
        <Text color="subdued" className="overflow-wrap-anywhere">
          {npsn}
        </Text>
        <Text color="subdued">{ksLabel}</Text>
        {confirmationStatus === 'UNCONFIRMED' && (
          <div className="flex items-center gap-1 mt-1">
            <Icon color="critical">warning</Icon>
            <Text color="critical">Perlu konfirmasi data.</Text>
          </div>
        )}
        {confirmationStatus === 'REJECTED' && (
          <Text color="critical">Ulangi konfirmasi data.</Text>
        )}
      </div>
      {cta.text && (
        <ButtonLink
          href={cta.href}
          variant="solid"
          size="lg"
          className="min-w-32 whitespace-nowrap flex-shrink-0"
          color={cta.shouldConfirm ? 'white' : 'black'}
          disabled={cta.isDisabled}
          onClick={cta.onClick}
        >
          {cta.text}
          <span className="sr-only">{schoolName}</span>
        </ButtonLink>
      )}
    </div>
  )
}

type ButtonLinkProps = ButtonProps & {
  href?: string
  children: React.ReactNode
  className?: string
  onClick?: () => void
}

function ButtonLink({ children, href, ...buttonProps }: ButtonLinkProps) {
  if (href)
    return (
      <Link href={href} passHref>
        <Button as="a" type={undefined} {...buttonProps}>
          {children}
        </Button>
      </Link>
    )

  return <Button {...buttonProps}>{children}</Button>
}
